// src/pages/Feeding/FeedingSchedulePage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, Form, Button, Row, Col, Alert, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave } from "@fortawesome/free-solid-svg-icons";
import "./FeedingSchedulePage.css";
import FOOD_LIBRARY from "../../data/foodLibrary.json";
import PetPageHeader from "../../components/PetPageHeader";
import { useSubscriptionStatus } from "../../lib/useSubscriptionStatus";
import { useNavigate } from "react-router-dom";
import FloatingPawButton from "../../components/FloatingPawButton";


const API_BASE = process.env.REACT_APP_API_BASE || "";
const FEEDING_AI_URL =
  "https://crza7ouixsd2llzb25bqvqejqq0hcdnb.lambda-url.us-east-1.on.aws/";

// --- API helpers -------------------------------------------------
async function apiGet(path) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`GET ${path} failed: ${res.status} ${text}`);
  }
  return res.json();
}

async function apiPut(path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`PUT ${path} failed: ${res.status} ${text}`);
  }
  return res.json();
}

function addDays(dateStr, days) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function getProductsForBrand(brand, species, category) {
  const norm = (v) => (v || "").toLowerCase();

  const b = norm(brand);
  const s = norm(species);
  const c = norm(category);

  return FOOD_LIBRARY.filter((f) => {
    if (b && norm(f.brand) !== b) return false;

    if (s && s !== "other" && norm(f.species) !== s) return false;
    if (c && norm(f.category) !== c) return false;

    return true;
  });
}


function pickDefaultFoodFromLibrary(species, category) {
  const norm = (v) => (v || "").toLowerCase();
  const s = norm(species);
  const c = norm(category);

  // 1) Try exact match: species + category
  let list = FOOD_LIBRARY.filter((f) => {
    if (s && s !== "other" && norm(f.species) !== s) return false;
    if (c && norm(f.category) !== c) return false;
    return true;
  });

  // 2) Fallback: species only
  if (!list.length) {
    list = FOOD_LIBRARY.filter((f) => {
      if (s && s !== "other" && norm(f.species) !== s) return false;
      return true;
    });
  }

  // 3) Fallback: anything
  if (!list.length) list = FOOD_LIBRARY;

  return list[0] || null;
}


// 🚫 Content filter – keep Feeding AI focused on pets & food
function isBlockedFeedingMessage(raw) {
  if (!raw) return false;
  const text = raw.toLowerCase();

  const petContextKeywords = [
    "dog",
    "cat",
    "puppy",
    "pup",
    "kitten",
    "pet",
    "my dog",
    "my cat",
    "my puppy",
    "my kitten",
    "neuter",
    "spay",
    "spayed",
    "neutered",
    "fixed",
    "in heat",
    "heat cycle",
    "breeding",
    "breed",
    "mating",
    "litter",
    "stud",
    "food",
    "kibble",
    "treats",
    "diet",
    "feeding",
    "calories",
  ];

  const sexualKeywords = [
    "sex",
    "sexual",
    "porn",
    "porno",
    "xxx",
    "onlyfans",
    "nsfw",
    "nude",
    "nudes",
    "blowjob",
    "bj",
    "anal",
    "threesome",
    "stripper",
    "camgirl",
  ];

  const selfHarm = ["kill myself", "suicide", "kys"];
  const extremeViolence = ["behead", "decapitate", "torture"];

  const hasSexual = sexualKeywords.some((kw) => text.includes(kw));
  const hasPetContext = petContextKeywords.some((kw) => text.includes(kw));

  // Allow “sex-ish” words that are clearly in a pet context (neuter/spay/etc.)
  if (hasSexual && hasPetContext) {
    return false;
  }

  if (hasSexual && !hasPetContext) return true;
  if (selfHarm.some((kw) => text.includes(kw))) return true;
  if (extremeViolence.some((kw) => text.includes(kw))) return true;

  return false;
}

// --- Helpers to build 3 AI options from a single base plan -------------
function scaleScheduleCups(schedule = {}, factor = 1) {
  const mealsPerDay = Number(schedule.mealsPerDay ?? 2) || 2;

  let totalDailyCups = Number(schedule.totalDailyCups ?? 0);

  // Rebuild totalDailyCups from legacy fields if missing
  if (!totalDailyCups) {
    const morning = Number(schedule.morningCups ?? 0);
    const evening = Number(schedule.eveningCups ?? 0);
    if (morning || evening) {
      totalDailyCups = morning + evening;
    }
  }

  // Nothing to work with
  if (!totalDailyCups || totalDailyCups <= 0) {
    return {
      mealsPerDay,
      totalDailyCups: 0,
      perMealCups: 0,
      mealCups: [],
      morningCups: 0,
      eveningCups: 0,
    };
  }

  // Apply scaling
  const scaledTotal = Math.max(
    0.1,
    Number((totalDailyCups * factor).toFixed(2))
  );

  // Per-meal split
  const perMealCups = Number(
    (scaledTotal / mealsPerDay).toFixed(2)
  );

  const mealCups = Array.from(
    { length: mealsPerDay },
    () => perMealCups
  );

  // Legacy compatibility
  let morningCups = 0;
  let eveningCups = 0;

  if (mealsPerDay === 1) {
    morningCups = perMealCups;
  } else if (mealsPerDay === 2) {
    morningCups = perMealCups;
    eveningCups = perMealCups;
  } else {
    // 3+ meals: keep morning/evening for summary only
    morningCups = perMealCups;
    eveningCups = perMealCups;
  }

  return {
    ...schedule,
    mealsPerDay,
    totalDailyCups: scaledTotal,
    perMealCups,
    mealCups, // 🔥 NEW — UI should use this
    morningCups,
    eveningCups,
  };
}
function getMealLabels(mealsPerDay) {
  if (mealsPerDay === 1) return ["Meal"];
  if (mealsPerDay === 2) return ["Morning", "Evening"];
  if (mealsPerDay === 3) return ["Morning", "Afternoon", "Evening"];
  if (mealsPerDay === 4) return ["Breakfast", "Lunch", "Dinner", "Evening"];
  return Array.from({ length: mealsPerDay }, (_, i) => `Meal ${i + 1}`);
}

function getMealCupsFromSchedule(schedule = {}) {
  const meals = Number(schedule.mealsPerDay ?? 2) || 2;

  // Prefer explicit array if present
  if (Array.isArray(schedule.mealCups) && schedule.mealCups.length) {
    return schedule.mealCups.map((n) => Number(n) || 0);
  }

  // Otherwise derive from total
  const total =
    Number(schedule.totalDailyCups ?? 0) ||
    (Number(schedule.morningCups ?? 0) + Number(schedule.eveningCups ?? 0));

  if (!total || total <= 0) return [];

  const perMeal = Number((total / meals).toFixed(2));
  return Array.from({ length: meals }, () => perMeal);
}


function getAutoFoodExplanationOptionA() {
  return (
    "No food selected — Paw chose a starter food for you.\n" +
    "Based on your pet’s weight and goal, this plan maintains a healthy daily intake.\n" +
    "You can change the food anytime before saving."
  );
}



function buildPlanOptionsFromBase(basePlan, goal) {
  let schedule = basePlan.schedule || {};
  const foodProfile = basePlan.foodProfile || {};
  const calorieProfile = basePlan.calorieProfile || {};

    const mealsPerDay =
    Number(schedule.mealsPerDay ?? calorieProfile.mealsPerDay ?? 2) || 2;

  // Derive a totalDailyCups no matter what
  let totalDailyCups = Number(schedule.totalDailyCups ?? 0);

  if (!totalDailyCups) {
    const morning = Number(schedule.morningCups ?? 0);
    const evening = Number(schedule.eveningCups ?? 0);
    if (morning || evening) totalDailyCups = morning + evening;
  }

  if (!totalDailyCups && calorieProfile.targetCalories && foodProfile.caloriesPerCup) {
    totalDailyCups =
      Number(calorieProfile.targetCalories) / Number(foodProfile.caloriesPerCup);
  }

  // Normalize schedule into a consistent shape
  if (!totalDailyCups || totalDailyCups <= 0) {
    schedule = {
      ...schedule,
      mealsPerDay,
      totalDailyCups: 0,
      perMealCups: 0,
      mealCups: [],
      morningCups: Number(schedule.morningCups ?? 0),
      eveningCups: Number(schedule.eveningCups ?? 0),
    };
  } else {
    const perMealCups = Number((totalDailyCups / mealsPerDay).toFixed(2));
    const mealCups = Array.from({ length: mealsPerDay }, () => perMealCups);

    // Legacy compatibility fields
    const legacyMorning = perMealCups;
    const legacyEvening = mealsPerDay >= 2 ? perMealCups : 0;

    schedule = {
      ...schedule,
      mealsPerDay,
      totalDailyCups: Number(totalDailyCups.toFixed(2)),
      perMealCups,
      mealCups,
      morningCups: legacyMorning,
      eveningCups: legacyEvening,
    };
  }



  const foodProfileFinal = foodProfile;
  const calorieProfileFinal = {
    ...calorieProfile,
  };

  const trimmedGoal = goal || calorieProfileFinal.goal || "maintain";

  let configs;
  if (trimmedGoal === "lose") {
    configs = [
      {
        id: "gentle-lose",
        label: "Gentle weight loss",
        factor: 0.9,
        tagline: "Small cut to portions for a slow, steady loss.",
      },
      {
        id: "moderate-lose",
        label: "Moderate weight loss",
        factor: 0.8,
        tagline: "Bigger cut — only if your vet agrees.",
      },
      {
        id: "very-gentle-lose",
        label: "Very gradual loss",
        factor: 0.95,
        tagline: "Tiny change for pets that just need to slim a little.",
      },
    ];
  } else if (trimmedGoal === "gain") {
    configs = [
      {
        id: "gentle-gain",
        label: "Gentle weight gain",
        factor: 1.1,
        tagline: "Slight bump in portions to start.",
      },
      {
        id: "moderate-gain",
        label: "Moderate weight gain",
        factor: 1.2,
        tagline: "More aggressive increase — best with vet guidance.",
      },
      {
        id: "very-gentle-gain",
        label: "Very gradual gain",
        factor: 1.05,
        tagline: "Tiny increase for pets that are just a bit under weight.",
      },
    ];
  } else {
    configs = [
      {
        id: "slightly-lower",
        label: "Slightly lighter portions",
        factor: 0.95,
        tagline: "Keeps portions light if treats are common.",
      },
      {
        id: "balanced",
        label: "Balanced portions",
        factor: 1.0,
        tagline: "Straight from Paw’s recommended plan.",
      },
      {
        id: "slightly-higher",
        label: "Slightly larger portions",
        factor: 1.05,
        tagline: "For very active days or leaner pets.",
      },
    ];
  }

  return configs.map((cfg) => ({
    id: cfg.id,
    label: cfg.label,
    tagline: cfg.tagline,
    plan: {
      schedule: scaleScheduleCups(schedule, cfg.factor),
      foodProfile: foodProfileFinal,
      calorieProfile: {
        ...calorieProfileFinal,
        goal: trimmedGoal,
      },
    },
  }));
}

// --- main component ------------------------------------------------
export default function FeedingSchedulePage() {
  const navigate = useNavigate();
const goSubscribe = () => navigate("/subscribe");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [mode, setMode] = useState("simple"); // "simple" | "advanced"
  const [viewMode, setViewMode] = useState("summary"); // "summary" | "edit"

  // NEW: stepper state
  const [simpleStep, setSimpleStep] = useState(1);
  const [advancedStep, setAdvancedStep] = useState(1);
  const maxSimpleStep = 3;
  const maxAdvancedStep = 3;

  // Feeding schedule
  const [mealsPerDay, setMealsPerDay] = useState("");
  const [morningCups, setMorningCups] = useState("");
  const [eveningCups, setEveningCups] = useState("");

  // Food profile
  const [brand, setBrand] = useState("");
  const [productName, setProductName] = useState("");
  const [category, setCategory] = useState("dry");
  const [flavor, setFlavor] = useState("");
  const [bagSizeLbs, setBagSizeLbs] = useState("");
  const [bagOpenedDate, setBagOpenedDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [caloriesPerCup, setCaloriesPerCup] = useState("");
  const [allergiesText, setAllergiesText] = useState("");
  const [preferredStore, setPreferredStore] = useState("amazon");

  // Pet + calorie inputs
  const [species, setSpecies] = useState("dog");
  const [weightLb, setWeightLb] = useState("");
  const [idealWeightLb, setIdealWeightLb] = useState("");
  const [lifeStage, setLifeStage] = useState("adult");
  const [activityLevel, setActivityLevel] = useState("normal");
  const [goal, setGoal] = useState("maintain");

  // Calorie profile (read-only-ish)
  const [maintenanceCalories, setMaintenanceCalories] = useState(null);
  const [targetCalories, setTargetCalories] = useState(null);
  const [currentCaloriesFromFood, setCurrentCaloriesFromFood] =
    useState(null);
  const [calorieDelta, setCalorieDelta] = useState(null);
  const [riskFlag, setRiskFlag] = useState("unknown");
  const [estimatedRunOutDate, setEstimatedRunOutDate] = useState(null);
  const [daysPerBag, setDaysPerBag] = useState(null);

  // Reminder preference
  const [foodReminderDaysBefore] = useState(3); // still here if you use it later
  const [foodReminderDate, setFoodReminderDate] = useState("");

  // AI assistant state
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");
  const [aiExplanation, setAiExplanation] = useState("");
  const [aiPlanOptions, setAiPlanOptions] = useState([]); // 3 options
  const [selectedPlanIndex, setSelectedPlanIndex] = useState(null);
  const [aiHighlightedFields, setAiHighlightedFields] = useState([]);

  // Search dropdown state
  const [brandFocused, setBrandFocused] = useState(false);
  const [productFocused, setProductFocused] = useState(false);

  // Affiliate link for reorder
  const [affiliateLink, setAffiliateLink] = useState("");
const [stepError, setStepError] = useState("");
const [freeAiUsed, setFreeAiUsed] = useState(false);


// ✨ "special" generating UI state
const [aiProgress, setAiProgress] = useState(0);
const [aiStage, setAiStage] = useState("");

const [aiJustFinished, setAiJustFinished] = useState(false);

useEffect(() => {
  if (aiProgress >= 100 && !aiLoading) {
    setAiJustFinished(true);
    const t = setTimeout(() => setAiJustFinished(false), 1200);
    return () => clearTimeout(t);
  }
}, [aiProgress, aiLoading]);
const aiListVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.05 },
  },
};

const aiItemVariants = {
  hidden: { opacity: 0, y: 10, scale: 0.98 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring", stiffness: 260, damping: 22 },
  },
  exit: { opacity: 0, y: 8, transition: { duration: 0.15 } },
};

const aiCompleteVariants = {
  hidden: { opacity: 0, y: -6, scale: 0.98 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring", stiffness: 300, damping: 18 },
  },
  exit: { opacity: 0, y: -6, transition: { duration: 0.2 } },
};


// Nice staged text
const AI_STAGES = useMemo(
  () => ["Analyzing pet profile…", "Checking food calories…", "Building 3 plan options…", "Finalizing…"],
  []
);

useEffect(() => {
  if (!aiLoading) {
    setAiProgress(0);
    setAiStage("");
    return;
  }

  let pct = 0;
  let stageIndex = 0;

  setAiStage(AI_STAGES[0]);

  const tick = setInterval(() => {
    // Smooth-ish ramp that slows down near the end
    const step = pct < 70 ? 6 : pct < 90 ? 3 : 1;
    pct = Math.min(97, pct + step);
    setAiProgress(pct);

    // Rotate stage text every ~1.2s
    stageIndex = (stageIndex + 1) % AI_STAGES.length;
    setAiStage(AI_STAGES[stageIndex]);
  }, 1200);

  return () => clearInterval(tick);
}, [aiLoading, AI_STAGES]);

  const canUseAi = Number(weightLb) > 0 && !!goal;
const { status: subStatus, loading: subLoading } = useSubscriptionStatus();

const isSubActive = subStatus === "active";



const canGenerateAi =
  canUseAi && (isSubActive || !freeAiUsed);

  const todayStr = useMemo(() => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  const petId = useMemo(
    () => (localStorage.getItem("lpp.tagId") || "").toUpperCase(),
    []
  );
  const petName = localStorage.getItem("lpp.petName") || "";
  const isSimple = mode === "simple";

  useEffect(() => {
  if (!petId) return;
  const key = `lpp.freeAiUsed.feeding.${petId}`;
  const v = localStorage.getItem(key);
  setFreeAiUsed(v === "1");
}, [petId]);

const markFreeAiUsed = () => {
  if (!petId) return;
  const key = `lpp.freeAiUsed.feeding.${petId}`;
  localStorage.setItem(key, "1");
  setFreeAiUsed(true);
};


  // AI requirements: weight + goal


  const foodsForSpecies = useMemo(() => {
    const s = (species || "").toLowerCase();
    if (!s || s === "other") return FOOD_LIBRARY;
    return FOOD_LIBRARY.filter(
      (f) => (f.species || "").toLowerCase() === s
    );
  }, [species]);

  const foodsForSpeciesAndCategory = useMemo(() => {
    const c = (category || "").toLowerCase();
    if (!c) return foodsForSpecies;
    return foodsForSpecies.filter(
      (f) => (f.category || "").toLowerCase() === c
    );
  }, [foodsForSpecies, category]);

  const UNIQUE_BRANDS = useMemo(
    () =>
      Array.from(
        new Set(
          foodsForSpeciesAndCategory.map((f) => f.brand).filter(Boolean)
        )
      ),
    [foodsForSpeciesAndCategory]
  );

  const UNIQUE_SPECIES = useMemo(
    () =>
      Array.from(
        new Set(FOOD_LIBRARY.map((f) => f.species).filter(Boolean))
      ),
    []
  );

  const UNIQUE_CATEGORIES = useMemo(
    () =>
      Array.from(
        new Set(foodsForSpecies.map((f) => f.category || "other"))
      ),
    [foodsForSpecies]
  );

  // NEW: reset step when switching modes
  useEffect(() => {
    if (mode === "simple") {
      setSimpleStep(1);
    } else {
      setAdvancedStep(1);
    }
  }, [mode]);

  const currentStep = isSimple ? simpleStep : advancedStep;
  const maxStep = isSimple ? maxSimpleStep : maxAdvancedStep;
useEffect(() => {
  if (subLoading) return;
  if (!isSubActive && mode !== "simple") {
    setMode("simple");
  }
}, [isSubActive, subLoading, mode]);

  const goToNextStep = () => {
    if (isSimple) {
      setSimpleStep((s) => Math.min(maxSimpleStep, s + 1));
    } else {
      setAdvancedStep((s) => Math.min(maxAdvancedStep, s + 1));
    }
  };

  const goToPrevStep = () => {
    if (isSimple) {
      setSimpleStep((s) => Math.max(1, s - 1));
    } else {
      setAdvancedStep((s) => Math.max(1, s - 1));
    }
  };

const isValidWeight = () => Number(weightLb) > 0;

const validateBeforeNext = () => {
  setStepError("");

  // Simple Step 1 includes weight -> must be valid to proceed
  if (isSimple && simpleStep === 1 && !isValidWeight()) {
    return {
      ok: false,
      msg: "Weight is required to continue. Please enter your pet’s current weight (lb).",
    };
  }

  // Advanced doesn’t have a "next" after step 3, but if you ever add steps later:
  if (!isSimple && advancedStep === 3 && !isValidWeight()) {
    return {
      ok: false,
      msg: "Weight is required to continue. Please enter your pet’s current weight (lb).",
    };
  }

  return { ok: true };
};

const handleNextStep = () => {
  const v = validateBeforeNext();
  if (!v.ok) {
    setStepError(v.msg);
    return;
  }
  goToNextStep();
};



const totalDailyCups = useMemo(() => {
  const m = Number(morningCups) || 0;
  const e = Number(eveningCups) || 0;
  const mpd = Number(mealsPerDay) || 0;

  if (!mpd) return m + e;

  // 1 meal: morning is the meal
  if (mpd === 1) return m;

  // 2 meals: morning + evening
  if (mpd === 2) return m + e;

  // 3+ meals: treat morningCups as "per-meal" (what you’re showing)
  // total = per-meal * mealsPerDay
  return Number((m * mpd).toFixed(2));
}, [morningCups, eveningCups, mealsPerDay]);

  // quick client-side days-per-bag estimate
  useEffect(() => {
    const bag = Number(bagSizeLbs) || 0;
    if (!bag || !totalDailyCups) {
      setDaysPerBag(null);
      setEstimatedRunOutDate(null);
      return;
    }
    const cupsPerLb = 4;
    const bagCups = bag * cupsPerLb;
    const days = Math.round(bagCups / totalDailyCups);
    setDaysPerBag(days > 0 ? days : null);
    if (bagOpenedDate && days > 0) {
      setEstimatedRunOutDate(addDays(bagOpenedDate, days));
    }
  }, [bagSizeLbs, totalDailyCups, bagOpenedDate]);

  // When brand + productName match something in the library,
  useEffect(() => {
    if (!brand || !productName) return;

    const match = FOOD_LIBRARY.find(
      (item) =>
        (item.brand || "").toLowerCase() === brand.toLowerCase() &&
        (item.productName || "").toLowerCase() ===
          productName.toLowerCase() &&
        (item.species || "").toLowerCase() ===
          (species || "").toLowerCase() &&
        (item.category || "").toLowerCase() ===
          (category || "").toLowerCase()
    );

    if (!match) return;

    if (match.category && category !== match.category) {
      setCategory(match.category);
    }

    if (
      match.caloriesPerCup &&
      String(caloriesPerCup) !== String(match.caloriesPerCup)
    ) {
      setCaloriesPerCup(String(match.caloriesPerCup));
    }

    if (match.species && (!species || species === "dog" || species === "cat")) {
      setSpecies(match.species);
    }
  }, [brand, productName, species, category, caloriesPerCup]);

  const filteredBrands = useMemo(() => {
    if (!brand) return UNIQUE_BRANDS.slice(0, 8);
    const q = brand.toLowerCase();
    return UNIQUE_BRANDS.filter((b) => b.toLowerCase().includes(q)).slice(
      0,
      8
    );
  }, [brand, UNIQUE_BRANDS]);

  const filteredProducts = useMemo(() => {
    const list = foodsForSpeciesAndCategory.filter((item) => {
      if (
        brand &&
        (item.brand || "").toLowerCase() !== brand.toLowerCase()
      ) {
        return false;
      }
      return true;
    });

    if (!productName) return list.slice(0, 8);

    const q = productName.toLowerCase();
    return list
      .filter((item) =>
        (item.productName || "").toLowerCase().includes(q)
      )
      .slice(0, 8);
  }, [foodsForSpeciesAndCategory, brand, productName]);

  // Load existing plan
  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setError("");
        setSuccess("");

        if (!petId) {
          setError(
            "No petId / SmartTag ID found. Please sign in again or open your SmartTag first."
          );
          setViewMode("summary");
          return;
        }

        const path = `/pets/${petId}/feeding`;
        const resp = await apiGet(path);
        const feedingPlan = resp?.feedingPlan;

        if (feedingPlan) {
          const schedule = feedingPlan.schedule || {};
          const foodProfile = feedingPlan.foodProfile || {};
          const cal = feedingPlan.calorieProfile || {};
          const meta = feedingPlan.meta || {};

          // schedule
          if (schedule.mealsPerDay != null)
            setMealsPerDay(String(schedule.mealsPerDay));
          if (schedule.morningCups != null)
            setMorningCups(String(schedule.morningCups));
          if (schedule.eveningCups != null)
            setEveningCups(String(schedule.eveningCups));

          // foodProfile
          if (foodProfile.brand != null) setBrand(foodProfile.brand);
          if (foodProfile.productName != null)
            setProductName(foodProfile.productName);
          if (foodProfile.category != null) setCategory(foodProfile.category);
          if (foodProfile.flavor != null) setFlavor(foodProfile.flavor);
          if (foodProfile.bagSizeLbs != null)
            setBagSizeLbs(String(foodProfile.bagSizeLbs));
          if (foodProfile.bagOpenedDate != null)
            setBagOpenedDate(foodProfile.bagOpenedDate);
          if (foodProfile.caloriesPerCup != null)
            setCaloriesPerCup(String(foodProfile.caloriesPerCup));
          if (Array.isArray(foodProfile.allergies)) {
            setAllergiesText(foodProfile.allergies.join(", "));
          }
          if (foodProfile.preferredStore != null)
            setPreferredStore(foodProfile.preferredStore);
          if (foodProfile.estimatedRunOutDate != null)
            setEstimatedRunOutDate(foodProfile.estimatedRunOutDate);
          if (foodProfile.affiliateLink != null)
            setAffiliateLink(foodProfile.affiliateLink);

          // calorieProfile
          if (cal.species) setSpecies(cal.species);
          if (cal.weightLb != null) setWeightLb(String(cal.weightLb));
          if (cal.idealWeightLb != null)
            setIdealWeightLb(String(cal.idealWeightLb));
          if (cal.lifeStage) setLifeStage(cal.lifeStage);
          if (cal.activityLevel) setActivityLevel(cal.activityLevel);
          if (cal.goal) setGoal(cal.goal);
          if (cal.maintenanceCalories != null)
            setMaintenanceCalories(cal.maintenanceCalories);
          if (cal.targetCalories != null)
            setTargetCalories(cal.targetCalories);
          if (cal.currentCaloriesFromFood != null)
            setCurrentCaloriesFromFood(cal.currentCaloriesFromFood);
          if (cal.calorieDelta != null) setCalorieDelta(cal.calorieDelta);
          if (cal.riskFlag) setRiskFlag(cal.riskFlag);

          // meta: one-time reminder date
          if (meta.foodReminderDate) {
            const rawDate = meta.foodReminderDate; // "YYYY-MM-DD"
            if (rawDate >= todayStr) {
              setFoodReminderDate(rawDate);
            } else {
              setFoodReminderDate("");
            }
          } else {
            setFoodReminderDate("");
          }

          setViewMode("summary");
        } else {
          setViewMode("edit");
        }
      } catch (err) {
        console.error("Feeding load failed:", err);
        setError("We couldn’t load your feeding plan. Please try again.");
        setViewMode("summary");
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [petId, todayStr]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSuccess("");
    setError("");
    setAiHighlightedFields([]);

    try {
      if (!petId) {
        setError(
          "No petId / SmartTag ID found. Please sign in again or open your SmartTag first."
        );
        return;
      }

      const allergiesArray = allergiesText
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      const payload = {
        petId,
        petName: petName || null,
        feedingSchedule: {
          mealsPerDay: Number(mealsPerDay) || 0,
          morningCups: Number(morningCups) || 0,
          eveningCups: Number(eveningCups) || 0,
          totalDailyCups: Number(totalDailyCups) || 0,
        },
        foodProfile: {
          brand: brand || null,
          productName: productName || null,
          category: category || null,
          flavor: flavor || null,
          bagSizeLbs: Number(bagSizeLbs) || null,
          bagOpenedDate: bagOpenedDate || null,
          caloriesPerCup: Number(caloriesPerCup) || null,
          allergies: allergiesArray,
          preferredStore: preferredStore || null,
          vendor: null,
          affiliateLink: null, // server will recompute
          autoShipEnabled: false,
          autoShipIntervalDays: null,
        },
        calorieInputs: {
          species: species || "dog",
          weightLb: Number(weightLb) || null,
          idealWeightLb: Number(idealWeightLb) || null,
          lifeStage: lifeStage || "adult",
          activityLevel: activityLevel || "normal",
          goal: goal || "maintain",
        },
        meta: {
          foodReminderDate: foodReminderDate || null,
        },
      };

      const path = `/pets/${petId}/feeding`;
      const resp = await apiPut(path, payload);

      const feedingPlan = resp?.feedingPlan;
      if (feedingPlan?.calorieProfile) {
        const cal = feedingPlan.calorieProfile;
        setMaintenanceCalories(cal.maintenanceCalories ?? null);
        setTargetCalories(cal.targetCalories ?? null);
        setCurrentCaloriesFromFood(cal.currentCaloriesFromFood ?? null);
        setCalorieDelta(cal.calorieDelta ?? null);
        setRiskFlag(cal.riskFlag || "unknown");
      }
      if (feedingPlan?.foodProfile?.estimatedRunOutDate) {
        setEstimatedRunOutDate(feedingPlan.foodProfile.estimatedRunOutDate);
      }
      if (feedingPlan?.foodProfile?.affiliateLink) {
        setAffiliateLink(feedingPlan.foodProfile.affiliateLink);
      }

      setSuccess("Feeding plan saved.");
      setViewMode("summary");
    } catch (err) {
      console.error("Feeding save failed:", err);
      setError("We couldn’t save your feeding plan. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateWithAI = async () => {
    try {
      setAiLoading(true);
       setAiProgress(5);
       setAiStage("Warming up Paw’s brain…");
      setAiError("");
      setAiExplanation("");
      setAiPlanOptions([]);
      setSelectedPlanIndex(null);
      setAiHighlightedFields([]);
      let autoFoodNote = "";
let brandToUse = brand;
let productToUse = productName;
let categoryToUse = category || "dry";
let speciesToUse = species || "dog";
let flavorToUse = flavor;
let caloriesPerCupToUse = caloriesPerCup;

// If no food selected, pick a real one from the library
if (!brandToUse || !productToUse) {
  const picked = pickDefaultFoodFromLibrary(speciesToUse, categoryToUse);

  if (picked) {
    brandToUse = picked.brand || brandToUse;
    productToUse = picked.productName || productToUse;
    flavorToUse = picked.flavor || flavorToUse;
    categoryToUse = picked.category || categoryToUse;
    speciesToUse = picked.species || speciesToUse;

    if (!caloriesPerCupToUse && picked.caloriesPerCup) {
      caloriesPerCupToUse = String(picked.caloriesPerCup);
    }

    // Update UI so user sees what Paw picked
    setBrand(brandToUse || "");
    setProductName(productToUse || "");
    setFlavor(flavorToUse || "");
    setCategory(categoryToUse || "dry");
    setSpecies(speciesToUse || "dog");
    if (caloriesPerCupToUse && !caloriesPerCup) {
      setCaloriesPerCup(String(caloriesPerCupToUse));
    }

    autoFoodNote =
      "No food selected — Paw picked a starter food from the library. You can change it anytime.";
  }
}


      if (!petId) {
        setAiError(
          "No petId / SmartTag found. Please open your SmartTag first."
        );
        return;
      }

      const combinedText = [petName, brandToUse, productToUse, flavorToUse]
        .filter(Boolean)
        .join(" ");
      if (isBlockedFeedingMessage(combinedText)) {
        setAiError(
          "I’m only allowed to help with pets, feeding, and basic diet guidance here. 🐾"
        );
        return;
      }

      if (!weightLb || Number(weightLb) <= 0) {
        setAiError(
          "Before Paw can build plans, please add your pet’s weight so the portions are sized correctly."
        );
        return;
      }

      const allergiesArray = allergiesText
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      const currentFeedingPlan = {
        schedule: {
          mealsPerDay: Number(mealsPerDay) || 0,
          morningCups: Number(morningCups) || 0,
          eveningCups: Number(eveningCups) || 0,
          totalDailyCups: totalDailyCups || 0,
        },
        foodProfile: {
  brand: brandToUse || null,
  productName: productToUse || null,
  category: categoryToUse || null,
  flavor: flavorToUse || null,
  bagSizeLbs: Number(bagSizeLbs) || null,
  bagOpenedDate: bagOpenedDate || null,
  caloriesPerCup: Number(caloriesPerCupToUse) || null,
  allergies: allergiesArray,
  preferredStore: preferredStore || null,
  vendor: null,
  affiliateLink: null,
  autoShipEnabled: false,
  autoShipIntervalDays: null,
},

        calorieProfile: null,
        meta: null,
      };

      const petProfile = {
  species: speciesToUse,
  weightLb: Number(weightLb) || null,
  idealWeightLb: Number(idealWeightLb) || null,
  lifeStage,
  activityLevel,
  goal,
};


      const resp = await fetch(FEEDING_AI_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          petId,
          petProfile,
          currentFeedingPlan,
          notes: "",
          strictMode: true,
          mode: "feeding-assistant",
          detailLevel: isSimple ? "simple" : "advanced",
        }),
      });

      if (!resp.ok) {
        const text = await resp.text();
        console.error("Feeding AI error:", resp.status, text);
        throw new Error("Feeding AI request failed");
      }

      const data = await resp.json();
      const suggested = data.suggestedFeedingPlan;

      if (!suggested) {
        setAiError(
          "I couldn’t build a safe feeding plan from that. Try adding weight and saving your food, then ask again."
        );
        return;
      }

      const options = buildPlanOptionsFromBase(suggested, goal);
      if (!options.length) {
        setAiError(
          "I generated a plan but couldn’t build options from it. Please try again."
        );
        return;
      }

      setAiPlanOptions(options);

const defaultIndex = options.length > 1 ? 1 : 0;
setSelectedPlanIndex(defaultIndex);

// ✅ IMPORTANT: actually apply it to the form state so Save matches selection
if (options[defaultIndex]?.plan) {
  applyPlanToForm(options[defaultIndex].plan);
}

if (!isSubActive && !freeAiUsed) {
  markFreeAiUsed();
}


      const foodFullName = [brandToUse, productToUse].filter(Boolean).join(" ");
const hasFoodName = !!foodFullName;

if (autoFoodNote) {
  setAiExplanation(getAutoFoodExplanationOptionA());
} else {
  const goalLine =
    goal === "lose"
      ? "Paw adjusted portions to support gradual weight loss."
      : goal === "gain"
      ? "Paw increased portions to support healthy weight gain."
      : "Paw built a balanced plan to maintain current weight.";

 setAiExplanation(
    <>
      {hasFoodName && (
        <>
          Using <strong>{foodFullName}</strong>,{" "}
        </>
      )}
      {goalLine}
      <br />
      Portions below are evenly divided per meal based on your schedule.
    </>
  );
}


   setAiProgress(100);
    } catch (err) {
      console.error("Feeding AI error:", err);
      setAiError("We couldn’t generate plans right now. Please try again.");
    } finally {
      setAiLoading(false);
    }
  };

  const applyPlanToForm = (plan) => {
    if (!plan) return;

    const updatedFields = [];
    const { schedule, foodProfile, calorieProfile } = plan;

 if (schedule) {
  if (schedule.mealsPerDay != null) {
    setMealsPerDay(String(schedule.mealsPerDay));
    updatedFields.push("mealsPerDay");
  }

  // 🔥 SOURCE OF TRUTH
  if (schedule.totalDailyCups != null) {
    const total = Number(schedule.totalDailyCups);

    // derive per-meal
    const meals = Number(schedule.mealsPerDay) || 1;
    const perMeal = meals > 0 ? Number((total / meals).toFixed(2)) : 0;

    // store per-meal in morningCups for UI
    setMorningCups(String(perMeal));
    setEveningCups(meals === 2 ? String(perMeal) : ""); // optional
    updatedFields.push("morningCups", "eveningCups");
  }
}


    if (foodProfile) {
      if (foodProfile.brand != null) {
        setBrand(foodProfile.brand);
        updatedFields.push("brand");
      }
      if (foodProfile.productName != null) {
        setProductName(foodProfile.productName);
        updatedFields.push("productName");
      }
      if (foodProfile.category != null) {
        setCategory(foodProfile.category);
        updatedFields.push("category");
      }
      if (foodProfile.flavor != null) {
        setFlavor(foodProfile.flavor);
        updatedFields.push("flavor");
      }
      if (foodProfile.bagSizeLbs != null) {
        setBagSizeLbs(String(foodProfile.bagSizeLbs));
        updatedFields.push("bagSizeLbs");
      }
      if (foodProfile.bagOpenedDate != null) {
        setBagOpenedDate(foodProfile.bagOpenedDate);
        updatedFields.push("bagOpenedDate");
      }
      if (foodProfile.caloriesPerCup != null) {
        setCaloriesPerCup(String(foodProfile.caloriesPerCup));
        updatedFields.push("caloriesPerCup");
      }
      if (Array.isArray(foodProfile.allergies)) {
        setAllergiesText(foodProfile.allergies.join(", "));
        updatedFields.push("allergiesText");
      }
      if (foodProfile.preferredStore != null) {
        setPreferredStore(foodProfile.preferredStore);
        updatedFields.push("preferredStore");
      }
    }

    if (calorieProfile) {
      if (calorieProfile.species) {
        setSpecies(calorieProfile.species);
        updatedFields.push("species");
      }
      if (calorieProfile.weightLb != null) {
        setWeightLb(String(calorieProfile.weightLb));
        updatedFields.push("weightLb");
      }
      if (calorieProfile.idealWeightLb != null) {
        setIdealWeightLb(String(calorieProfile.idealWeightLb));
        updatedFields.push("idealWeightLb");
      }
      if (calorieProfile.lifeStage) {
        setLifeStage(calorieProfile.lifeStage);
        updatedFields.push("lifeStage");
      }
      if (calorieProfile.activityLevel) {
        setActivityLevel(calorieProfile.activityLevel);
        updatedFields.push("activityLevel");
      }
      if (calorieProfile.goal) {
        setGoal(calorieProfile.goal);
        updatedFields.push("goal");
      }
    }

    setAiHighlightedFields(updatedFields);
  };

  const handleSelectPlan = (index) => {
    setSelectedPlanIndex(index);
    const opt = aiPlanOptions[index];
    if (opt && opt.plan) {
      applyPlanToForm(opt.plan);
    }
  };

  const handleResetForm = () => {
    setMealsPerDay("");
    setMorningCups("");
    setEveningCups("");

    setBrand("");
    setProductName("");
    setCategory("dry");
    setFlavor("");
    setBagSizeLbs("");
    setBagOpenedDate(new Date().toISOString().slice(0, 10));
    setCaloriesPerCup("");
    setAllergiesText("");
    setPreferredStore("amazon");

    setSpecies("dog");
    setWeightLb("");
    setIdealWeightLb("");
    setLifeStage("adult");
    setActivityLevel("normal");
    setGoal("maintain");

    setMaintenanceCalories(null);
    setTargetCalories(null);
    setCurrentCaloriesFromFood(null);
    setCalorieDelta(null);
    setRiskFlag("unknown");
    setEstimatedRunOutDate(null);
    setDaysPerBag(null);
    setFoodReminderDate("");

    setAiError("");
    setAiExplanation("");
    setAiPlanOptions([]);
    setSelectedPlanIndex(null);
    setAiHighlightedFields([]);
    setAffiliateLink("");
    setSuccess("");
    setError("");

    // Reset stepper to start
    setSimpleStep(1);
    setAdvancedStep(1);
  };

  const riskMessage = useMemo(() => {
    if (riskFlag === "overfed") {
      return "Based on this plan, your pet may be getting more calories than recommended. Consider slightly reducing each meal or switching to a lower-calorie formula. Always confirm changes with your vet.";
    }
    if (riskFlag === "underfed") {
      return "This plan may be providing fewer calories than recommended. Consider increasing food or a more calorie-dense option, and check with your vet.";
    }
    if (riskFlag === "balanced") {
      return "Nice work! This feeding plan looks well-aligned with your pet’s size, age, and activity level.";
    }
    return "Add weight, life stage, and calories per cup to see more detailed guidance.";
  }, [riskFlag]);

  const hasPlan =
    !!mealsPerDay ||
    !!brand ||
    !!productName ||
    !!weightLb ||
    !!caloriesPerCup;

  const reminderSummary = useMemo(() => {
    if (!brand && !productName) {
      return "Select a food to enable reminders.";
    }

    if (!foodReminderDate) {
      return "No reminder set.";
    }

    const [y, m, d] = foodReminderDate.split("-");
    const pretty = `${m}/${d}/${y}`;
    return `One-time text check-in around ${pretty}.`;
  }, [foodReminderDate, brand, productName]);

  if (loading) {
    return (
      <div className="feeding-wrapper">
        <div className="feeding-shell">
          <Card className="mb-3">
            <Card.Body className="text-center">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading feeding &amp; food info…
            </Card.Body>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="feeding-wrapper">
      <div className="feeding-shell">
        <PetPageHeader
          baseTitle="Feeding & Food"
          subtitle="Keep meals consistent, track calories, and plan reorders."
        />

        <main className="feeding-body">
          {error && (
            <Alert
              variant="danger"
              onClose={() => setError("")}
              dismissible
              className="mb-3"
            >
              {error}
            </Alert>
          )}

          {/* SUMMARY VIEW */}
          {viewMode === "summary" && (
            <Card className="mb-3">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h5 className="mb-0">
                    {petName
                      ? `${petName}’s feeding plan`
                      : "Feeding summary"}
                  </h5>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className="feeding-secondary-btn"
                    onClick={() => setViewMode("edit")}
                  >
                    Edit
                  </Button>
                </div>

                {!hasPlan ? (
                  <>
                    <p className="small text-muted mb-2">
                      No feeding plan is saved yet for this SmartTag.
                    </p>
                    <Button
                      size="sm"
                      className="lpp-pill-btn"
                      onClick={() => setViewMode("edit")}
                    >
                      Set up feeding plan
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="feeding-summary-strip mb-2">
                      <div className="feeding-summary-pill">
                        <span className="label">Schedule</span>
                        <span className="value">
                          {mealsPerDay || "-"}{" "}
                          {mealsPerDay === "1" ? "meal" : "meals"} / day ·{" "}
                          {totalDailyCups || 0} cups / day
                        </span>
                      </div>

                      <div className="feeding-summary-pill">
                        <span className="label">Food</span>
                        <span className="value">
                          {brand || "Brand not set"}
                          {productName ? ` – ${productName}` : ""}
                        </span>
                      </div>

                      <div className="feeding-summary-pill">
                        <span className="label">Calories</span>
                        <span className="value">
                          {targetCalories != null
                            ? `${targetCalories} kcal/day`
                            : maintenanceCalories != null
                            ? `${maintenanceCalories} kcal/day`
                            : "Not calculated"}
                        </span>
                      </div>
                    </div>

                    {estimatedRunOutDate && (
                      <p className="small text-muted mb-1">
                        Based on this bag and schedule, we estimate it will run
                        out around <strong>{estimatedRunOutDate}</strong>
                        {daysPerBag ? ` (~${daysPerBag} days per bag).` : "."}
                      </p>
                    )}

                    <p className="small text-muted mb-1">
                      <strong>Food check-in reminder:</strong>{" "}
                      {reminderSummary}
                    </p>


                    <p className="small mb-2">
                      <strong>Pet:</strong>{" "}
                      {species || "Pet"}{" "}
                      {weightLb ? `· ${weightLb} lb` : ""}{" "}
                      {goal ? `· Goal: ${goal}` : ""}
                    </p>

                   {affiliateLink && (
  <>
    <Button
      as="a"
      href={affiliateLink}
      target="_blank"
      rel="noopener noreferrer"
      size="sm"
      className="lpp-pill-btn-outline d-inline-flex align-items-center gap-2"
    >
      <i className="fab fa-amazon" aria-hidden="true" />
      Shop food
    </Button>

   <div className="affiliate-disclosure">
  As an affiliate, Little Paws Place earns from qualifying purchases.
</div>

  </>
)}

                    <p className="small text-muted mt-3 mb-0">
                      {riskMessage}
                    </p>
                  </>
                )}
              </Card.Body>
            </Card>
          )}

          {/* EDIT VIEW */}
          {viewMode === "edit" && (
            <Card className="mb-3">
              <Card.Body>
                {/* Simple / Advanced toggle */}
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                  <div className="feeding-mode-toggle">
                    <button
                      type="button"
                      className={`feeding-mode-btn ${
                        mode === "simple" ? "active" : ""
                      }`}
                      onClick={() => setMode("simple")}
                    >
                      Simple
                    </button>
                   <button
                      type="button"
                      className={`feeding-mode-btn ${mode === "advanced" ? "active" : ""}`}
                      onClick={() => setMode("advanced")}
                      disabled={!isSubActive}
                      style={!isSubActive ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                    >
                      Advanced
                    </button>

                  </div>
                </div>

                {/* Stepper bar */}
                <div className="feeding-stepper mb-3">
                  {[1, 2, 3].map((stepNum) => (
                    <div
                      key={stepNum}
                      className={
                        "feeding-stepper-step" +
                        (currentStep === stepNum ? " active" : "") +
                        (currentStep > stepNum ? " complete" : "")
                      }
                    >
                      <div className="circle">{stepNum}</div>
                      <div className="label">
                        {isSimple
                          ? stepNum === 1
                            ? "Pet basics"
                            : stepNum === 2
                            ? "Food"
                            : "Review & AI"
                          : stepNum === 1
                          ? "Food & schedule"
                          : stepNum === 2
                          ? "Bag & calories"
                          : "Goals & AI"}
                      </div>
                    </div>
                  ))}
                </div>
                {stepError && (
  <Alert
    variant="warning"
    className="py-1 px-2 mb-3"
    onClose={() => setStepError("")}
    dismissible
  >
    <small>{stepError}</small>
  </Alert>
)}


                <Form onSubmit={handleSave}>
                  {/* SIMPLE MODE STEPS */}
                  {isSimple && (
                    <>
                      {/* STEP 1: meals per day + basic weight/goal */}
                      {simpleStep === 1 && (
                        <>
                          <Row className="mb-3 justify-content-center">
                            {/* Meals Per Day */}
                            <Col xs={12} md={5} className="mb-2">
                              <Form.Group>
                                <Form.Label>
                                  Meals per day (AI uses this)
                                </Form.Label>
                                <Form.Select
                                  value={mealsPerDay}
                                  className={
                                    aiHighlightedFields.includes(
                                      "mealsPerDay"
                                    )
                                      ? "ai-field"
                                      : ""
                                  }
                                  onChange={(e) =>
                                    setMealsPerDay(e.target.value)
                                  }
                                >
                                  <option value="">
                                    Let Paw Decide…
                                  </option>
                                  <option value={1}>1</option>
                                  <option value={2}>2</option>
                                  <option value={3}>3</option>
                                  <option value={4}>4</option>
                                </Form.Select>
                              </Form.Group>
                            </Col>

                            {/* Food reminder date */}
                            <Col xs={12} md={5} className="mb-2">
                              <Form.Group>
                                <Form.Label>
                                  Food check-in reminder
                                </Form.Label>
                                <div className="d-flex gap-2 align-items-center">
                                 <Form.Control
                                    type="date"
                                    value={foodReminderDate || ""}
                                    min={todayStr}
                                    onChange={(e) => setFoodReminderDate(e.target.value)}
                                    disabled={!isSubActive}
                                  />{!isSubActive && (
                                      <Form.Text className="text-muted">
                                        Reminders unlock with a{" "}
                                        <button
                                          type="button"
                                          className="btn btn-link p-0 align-baseline"
                                          onClick={goSubscribe}
                                        >
                                          subscription
                                        </button>
                                        .
                                      </Form.Text>

                                    )}


                                  {foodReminderDate && (
                                    <Button
                                      variant="outline-secondary"
                                      size="sm"
                                      type="button"
                                      onClick={() =>
                                        setFoodReminderDate("")
                                      }
                                    >
                                      Clear
                                    </Button>
                                  )}
                                </div>
                                <Form.Text className="text-muted">
                                  Paw will send a one-time text check-in
                                  around this date to remind you to check and
                                  reorder food.
                                </Form.Text>
                              </Form.Group>
                            </Col>
                          </Row>

                          {/* SIMPLE – Pet basics for AI */}
                          <Row className="mb-3 justify-content-center">
                            {/* Current Weight */}
                            <Col xs={12} md={5} className="mb-2">
                              <Form.Group>
                                <Form.Label>Current weight (lb)</Form.Label>
                          <Form.Control
                                type="number"
                                min="0"
                                step="0.5"
                                value={weightLb}
                                onChange={(e) => setWeightLb(e.target.value)}
                                isInvalid={isSimple && simpleStep === 1 && !!stepError && !isValidWeight()}
                              />
                              <Form.Control.Feedback type="invalid">
                                Weight is required.
                              </Form.Control.Feedback>

                              </Form.Group>
                            </Col>

                            {/* Goal */}
                            <Col xs={12} md={5} className="mb-2">
                              <Form.Group>
                                <Form.Label>Goal</Form.Label>
                                <Form.Select
                                  value={goal}
                                  onChange={(e) =>
                                    setGoal(e.target.value)
                                  }
                                >
                                  <option value="maintain">
                                    Maintain weight
                                  </option>
                                  <option value="lose">Lose weight</option>
                                  <option value="gain">Gain weight</option>
                                </Form.Select>
                              </Form.Group>
                            </Col>
                          </Row>
                        </>
                      )}

                      {/* STEP 2: species + category + brand/product */}
                      {simpleStep === 2 && (
                        <>
                          {/* SPECIES + CATEGORY */}
                          <Row className="mb-3 justify-content-center">
                            <Col xs={12} md={5} className="mb-2">
                              <Form.Group>
                                <Form.Label>Species</Form.Label>
                                <Form.Select
                                  value={species}
                                  onChange={(e) => {
                                    const next = e.target.value;
                                    setSpecies(next);
                                    setCategory("");
                                    setBrand("");
                                    setProductName("");
                                  }}
                                >
                                  <option value="">
                                    Select species…
                                  </option>
                                  {UNIQUE_SPECIES.map((sp) => (
                                    <option key={sp} value={sp}>
                                      {sp.charAt(0).toUpperCase() +
                                        sp.slice(1)}
                                    </option>
                                  ))}
                                </Form.Select>
                                <Form.Text className="text-muted">
                                  This filters categories, brands, and foods to
                                  match your pet.
                                </Form.Text>
                              </Form.Group>
                            </Col>

                            <Col xs={12} md={5} className="mb-2">
                              <Form.Group>
                                <Form.Label>Category</Form.Label>
                                <Form.Select
                                  value={category}
                                  onChange={(e) => {
                                    setCategory(e.target.value);
                                    setBrand("");
                                    setProductName("");
                                  }}
                                >
                                  <option value="">
                                    All categories
                                  </option>
                                  {UNIQUE_CATEGORIES.map((catKeyRaw) => {
                                    const catKey = catKeyRaw || "other";
                                    const labelMap = {
                                      dry: "Dry kibble",
                                      wet: "Wet / canned",
                                      raw: "Raw / fresh",
                                      mixed: "Mixed",
                                      other: "Other",
                                    };
                                    const label =
                                      labelMap[catKey] ||
                                      catKey.charAt(0).toUpperCase() +
                                        catKey.slice(1);
                                    return (
                                      <option
                                        key={catKey}
                                        value={catKey}
                                      >
                                        {label}
                                      </option>
                                    );
                                  })}
                                </Form.Select>
                              </Form.Group>
                            </Col>
                          </Row>

                          {/* BRAND + PRODUCT */}
                          <Row className="mb-3 justify-content-center">
                            {/* Brand */}
                            <Col xs={12} md={5} className="mb-2">
                              <Form.Group>
                                <Form.Label>Brand</Form.Label>
                                <div
                                  className="feeding-search-group"
                                  style={{ position: "relative" }}
                                >
                                  <Form.Control
                                    type="text"
                                    autoComplete="off"
                                    className={
                                      aiHighlightedFields.includes(
                                        "brand"
                                      )
                                        ? "feeding-search-input ai-field"
                                        : "feeding-search-input"
                                    }
                                    placeholder="Start typing a brand…"
                                    value={brand}
                                    onChange={(e) => {
                                      setBrand(e.target.value);
                                      setProductName("");
                                    }}
                                    onFocus={() =>
                                      setBrandFocused(true)
                                    }
                                    onBlur={() =>
                                      setTimeout(
                                        () => setBrandFocused(false),
                                        120
                                      )
                                    }
                                  />

                                  {brand && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setBrand("");
                                        setProductName("");
                                      }}
                                      className="feeding-clear-btn"
                                      style={{
                                        position: "absolute",
                                        right: "10px",
                                        top: "50%",
                                        transform:
                                          "translateY(-50%)",
                                        background: "transparent",
                                        border: "none",
                                        fontSize: "16px",
                                        cursor: "pointer",
                                        color: "#999",
                                      }}
                                    >
                                      ×
                                    </button>
                                  )}

                                  {brandFocused &&
                                    filteredBrands.length > 0 && (
                                      <div className="feeding-suggestions">
                                        {filteredBrands.map((b) => (
                                          <button
                                            type="button"
                                            key={b}
                                            className="feeding-suggestion-item"
                                            onMouseDown={() => {
                                              setBrand(b);
                                              setProductName("");
                                            }}
                                          >
                                            <div className="feeding-suggestion-title">
                                              {b}
                                            </div>
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                </div>
                              </Form.Group>
                            </Col>

                            {/* Product */}
                            <Col xs={12} md={5} className="mb-2">
                              <Form.Group>
                                <Form.Label>Product name</Form.Label>

                                <div className="feeding-search-group">
                                  <Form.Control
                                    type="text"
                                    autoComplete="off"
                                    className={
                                      aiHighlightedFields.includes(
                                        "productName"
                                      )
                                        ? "feeding-search-input ai-field"
                                        : "feeding-search-input"
                                    }
                                    placeholder="Search by product name…"
                                    value={productName}
                                    onChange={(e) =>
                                      setProductName(
                                        e.target.value
                                      )
                                    }
                                    onFocus={() =>
                                      setProductFocused(true)
                                    }
                                    onBlur={() =>
                                      setTimeout(
                                        () =>
                                          setProductFocused(false),
                                        120
                                      )
                                    }
                                  />

                                  {productFocused &&
                                    filteredProducts.length > 0 && (
                                      <div className="feeding-suggestions">
                                        {filteredProducts.map(
                                          (item) => (
                                            <button
                                              type="button"
                                              key={item.id}
                                              className="feeding-suggestion-item"
                                              onMouseDown={() => {
                                                setProductName(
                                                  item.productName
                                                );
                                                if (
                                                  item.brand &&
                                                  item.brand !==
                                                    brand
                                                ) {
                                                  setBrand(
                                                    item.brand
                                                  );
                                                }
                                                if (
                                                  item.category &&
                                                  item.category.toLowerCase() !==
                                                    (category ||
                                                      "").toLowerCase()
                                                ) {
                                                  setCategory(
                                                    item.category
                                                  );
                                                }
                                                if (
                                                  item.species &&
                                                  item.species.toLowerCase() !==
                                                    (species ||
                                                      "").toLowerCase()
                                                ) {
                                                  setSpecies(
                                                    item.species
                                                  );
                                                }
                                              }}
                                            >
                                              <div className="feeding-suggestion-title">
                                                {
                                                  item.productName
                                                }
                                              </div>
                                              <div className="feeding-suggestion-sub">
                                                {item.brand} ·{" "}
                                                {item.category ||
                                                  "Food"}{" "}
                                                · {item.species}
                                              </div>
                                            </button>
                                          )
                                        )}
                                      </div>
                                    )}
                                </div>
                              </Form.Group>
                            </Col>
                          </Row>

                          {(brand || productName) && (
                            <div className="mb-3">
                              {estimatedRunOutDate && (
                                <p className="small text-muted mb-1">
                                  Based on this bag and schedule, we
                                  estimate it will run out around{" "}
                                  <strong>
                                    {estimatedRunOutDate}
                                  </strong>
                                  .
                                </p>
                              )}

                              <div className="small mb-0 feeding-gold-pill">
                                <strong>
                                  {brand || "Brand not set"}
                                </strong>
                                {productName
                                  ? ` – ${productName}`
                                  : ""}
                                {bagSizeLbs
                                  ? ` (${bagSizeLbs} lb bag)`
                                  : ""}
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {/* STEP 3: review & AI (simple) */}
                      {simpleStep === 3 && (
                        <>
                          <h5 className="mt-3 text-center">
                            Review details
                          </h5>
                          <p className="small text-muted text-center mb-3">
                            Double-check your pet and food info, then let
                            Paw generate a few plan options and save your
                            feeding plan.
                          </p>
                        </>
                      )}
                    </>
                  )}

                  {/* ADVANCED MODE STEPS */}
                  {!isSimple && (
                    <>
                      {/* STEP 1: schedule + species/category/brand/product */}
                      {advancedStep === 1 && (
                        <>
                          <Row className="mb-3 justify-content-center">
                            {/* Meals Per Day */}
                            <Col xs={12} md={5} className="mb-2">
                              <Form.Group>
                                <Form.Label>
                                  Meals per day (AI uses this)
                                </Form.Label>
                                <Form.Select
                                  value={mealsPerDay}
                                  className={
                                    aiHighlightedFields.includes(
                                      "mealsPerDay"
                                    )
                                      ? "ai-field"
                                      : ""
                                  }
                                  onChange={(e) =>
                                    setMealsPerDay(e.target.value)
                                  }
                                >
                                  <option value="">
                                    Let Paw Decide…
                                  </option>
                                  <option value={1}>1</option>
                                  <option value={2}>2</option>
                                  <option value={3}>3</option>
                                  <option value={4}>4</option>
                                </Form.Select>
                              </Form.Group>
                            </Col>

                            {/* Food reminder date */}
                            <Col xs={12} md={5} className="mb-2">
                              <Form.Group>
                                <Form.Label>
                                  Food check-in reminder
                                </Form.Label>
                                <div className="d-flex gap-2 align-items-center">
                                  <Form.Control
                                    type="date"
                                    value={foodReminderDate || ""}
                                    min={todayStr}
                                    onChange={(e) =>
                                      setFoodReminderDate(e.target.value)
                                    }
                                  />
                                  {foodReminderDate && (
                                    <Button
                                      variant="outline-secondary"
                                      size="sm"
                                      type="button"
                                      onClick={() =>
                                        setFoodReminderDate("")
                                      }
                                    >
                                      Clear
                                    </Button>
                                  )}
                                </div>
                                <Form.Text className="text-muted">
                                  Paw will send a one-time text check-in
                                  around this date to remind you to check and
                                  reorder food.
                                </Form.Text>
                              </Form.Group>
                            </Col>
                          </Row>

                          {/* SPECIES + CATEGORY */}
                          <Row className="mb-3 justify-content-center">
                            <Col xs={12} md={5} className="mb-2">
                              <Form.Group>
                                <Form.Label>Species</Form.Label>
                                <Form.Select
                                  value={species}
                                  onChange={(e) => {
                                    const next = e.target.value;
                                    setSpecies(next);
                                    setCategory("");
                                    setBrand("");
                                    setProductName("");
                                  }}
                                >
                                  <option value="">
                                    Select species…
                                  </option>
                                  {UNIQUE_SPECIES.map((sp) => (
                                    <option key={sp} value={sp}>
                                      {sp.charAt(0).toUpperCase() +
                                        sp.slice(1)}
                                    </option>
                                  ))}
                                </Form.Select>
                                <Form.Text className="text-muted">
                                  This filters categories, brands, and foods to
                                  match your pet.
                                </Form.Text>
                              </Form.Group>
                            </Col>

                            <Col xs={12} md={5} className="mb-2">
                              <Form.Group>
                                <Form.Label>Category</Form.Label>
                                <Form.Select
                                  value={category}
                                  onChange={(e) => {
                                    setCategory(e.target.value);
                                    setBrand("");
                                    setProductName("");
                                  }}
                                >
                                  <option value="">
                                    All categories
                                  </option>
                                  {UNIQUE_CATEGORIES.map((catKeyRaw) => {
                                    const catKey = catKeyRaw || "other";
                                    const labelMap = {
                                      dry: "Dry kibble",
                                      wet: "Wet / canned",
                                      raw: "Raw / fresh",
                                      mixed: "Mixed",
                                      other: "Other",
                                    };
                                    const label =
                                      labelMap[catKey] ||
                                      catKey.charAt(0).toUpperCase() +
                                        catKey.slice(1);
                                    return (
                                      <option
                                        key={catKey}
                                        value={catKey}
                                      >
                                        {label}
                                      </option>
                                    );
                                  })}
                                </Form.Select>
                              </Form.Group>
                            </Col>
                          </Row>

                          {/* BRAND + PRODUCT */}
                          <Row className="mb-3 justify-content-center">
                            {/* Brand */}
                            <Col xs={12} md={5} className="mb-2">
                              <Form.Group>
                                <Form.Label>Brand</Form.Label>
                                <div
                                  className="feeding-search-group"
                                  style={{ position: "relative" }}
                                >
                                  <Form.Control
                                    type="text"
                                    autoComplete="off"
                                    className={
                                      aiHighlightedFields.includes(
                                        "brand"
                                      )
                                        ? "feeding-search-input ai-field"
                                        : "feeding-search-input"
                                    }
                                    placeholder="Start typing a brand…"
                                    value={brand}
                                    onChange={(e) => {
                                      setBrand(e.target.value);
                                      setProductName("");
                                    }}
                                    onFocus={() =>
                                      setBrandFocused(true)
                                    }
                                    onBlur={() =>
                                      setTimeout(
                                        () => setBrandFocused(false),
                                        120
                                      )
                                    }
                                  />

                                  {brand && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setBrand("");
                                        setProductName("");
                                      }}
                                      className="feeding-clear-btn"
                                      style={{
                                        position: "absolute",
                                        right: "10px",
                                        top: "50%",
                                        transform:
                                          "translateY(-50%)",
                                        background: "transparent",
                                        border: "none",
                                        fontSize: "16px",
                                        cursor: "pointer",
                                        color: "#999",
                                      }}
                                    >
                                      ×
                                    </button>
                                  )}

                                  {brandFocused &&
                                    filteredBrands.length > 0 && (
                                      <div className="feeding-suggestions">
                                        {filteredBrands.map((b) => (
                                          <button
                                            type="button"
                                            key={b}
                                            className="feeding-suggestion-item"
                                            onMouseDown={() => {
                                              setBrand(b);
                                              setProductName("");
                                            }}
                                          >
                                            <div className="feeding-suggestion-title">
                                              {b}
                                            </div>
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                </div>
                              </Form.Group>
                            </Col>

                            {/* Product */}
                            <Col xs={12} md={5} className="mb-2">
                              <Form.Group>
                                <Form.Label>Product name</Form.Label>
                                <div className="feeding-search-group">
                                  <Form.Control
                                    type="text"
                                    autoComplete="off"
                                    className={
                                      aiHighlightedFields.includes(
                                        "productName"
                                      )
                                        ? "feeding-search-input ai-field"
                                        : "feeding-search-input"
                                    }
                                    placeholder="Search by product name…"
                                    value={productName}
                                    onChange={(e) =>
                                      setProductName(
                                        e.target.value
                                      )
                                    }
                                    onFocus={() =>
                                      setProductFocused(true)
                                    }
                                    onBlur={() =>
                                      setTimeout(
                                        () =>
                                          setProductFocused(false),
                                        120
                                      )
                                    }
                                  />

                                  {productFocused &&
                                    filteredProducts.length > 0 && (
                                      <div className="feeding-suggestions">
                                        {filteredProducts.map(
                                          (item) => (
                                            <button
                                              type="button"
                                              key={item.id}
                                              className="feeding-suggestion-item"
                                              onMouseDown={() => {
                                                setProductName(
                                                  item.productName
                                                );
                                                if (
                                                  item.brand &&
                                                  item.brand !==
                                                    brand
                                                ) {
                                                  setBrand(
                                                    item.brand
                                                  );
                                                }
                                                if (
                                                  item.category &&
                                                  item.category.toLowerCase() !==
                                                    (category ||
                                                      "").toLowerCase()
                                                ) {
                                                  setCategory(
                                                    item.category
                                                  );
                                                }
                                                if (
                                                  item.species &&
                                                  item.species.toLowerCase() !==
                                                    (species ||
                                                      "").toLowerCase()
                                                ) {
                                                  setSpecies(
                                                    item.species
                                                  );
                                                }
                                              }}
                                            >
                                              <div className="feeding-suggestion-title">
                                                {
                                                  item.productName
                                                }
                                              </div>
                                              <div className="feeding-suggestion-sub">
                                                {item.brand} ·{" "}
                                                {item.category ||
                                                  "Food"}{" "}
                                                · {item.species}
                                              </div>
                                            </button>
                                          )
                                        )}
                                      </div>
                                    )}
                                </div>
                              </Form.Group>
                            </Col>
                          </Row>

                          {(brand || productName) && (
                            <div className="mb-3">
                              {estimatedRunOutDate && (
                                <p className="small text-muted mb-1">
                                  Based on this bag and schedule, we
                                  estimate it will run out around{" "}
                                  <strong>
                                    {estimatedRunOutDate}
                                  </strong>
                                  .
                                </p>
                              )}

                              <div className="small mb-0 feeding-gold-pill">
                                <strong>
                                  {brand || "Brand not set"}
                                </strong>
                                {productName
                                  ? ` – ${productName}`
                                  : ""}
                                {bagSizeLbs
                                  ? ` (${bagSizeLbs} lb bag)`
                                  : ""}
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {/* STEP 2: Bag & calories & allergies */}
                      {advancedStep === 2 && (
                        <>
                          <h5 className="mt-3 text-center">
                            Bag &amp; calories (advanced)
                          </h5>

                          <Row className="mb-3 justify-content-center">
                            <Col xs={12} md={5} className="mb-2">
                              <Form.Group>
                                <Form.Label>Bag size (lbs)</Form.Label>
                                <Form.Control
                                  type="number"
                                  min="0"
                                  step="0.5"
                                  value={bagSizeLbs}
                                  onChange={(e) =>
                                    setBagSizeLbs(e.target.value)
                                  }
                                />
                              </Form.Group>
                            </Col>

                            <Col xs={12} md={5} className="mb-2">
                              <Form.Group>
                                <Form.Label>Date bag opened</Form.Label>
                                <Form.Control
                                  type="date"
                                  value={bagOpenedDate}
                                  onChange={(e) =>
                                    setBagOpenedDate(e.target.value)
                                  }
                                />
                              </Form.Group>
                            </Col>
                          </Row>

                          <Row className="mb-3 justify-content-center">
                            <Col xs={12} md={5} className="mb-2">
                              <Form.Group>
                                <Form.Label>Calories per cup</Form.Label>
                                <Form.Control
                                  type="number"
                                  min="0"
                                  step="1"
                                  value={caloriesPerCup}
                                  onChange={(e) =>
                                    setCaloriesPerCup(e.target.value)
                                  }
                                />
                                <Form.Text className="text-muted">
                                  Usually listed on the bag (kcal/cup).
                                </Form.Text>
                              </Form.Group>
                            </Col>

                            <Col xs={12} md={5} className="mb-2">
                              <Form.Group>
                                <Form.Label>
                                  Allergies / sensitivities
                                </Form.Label>
                                <Form.Control
                                  type="text"
                                  placeholder="e.g., chicken, beef, grain-free only"
                                  value={allergiesText}
                                  onChange={(e) =>
                                    setAllergiesText(e.target.value)
                                  }
                                />
                                <Form.Text className="text-muted">
                                  Separate multiple with commas.
                                </Form.Text>
                              </Form.Group>
                            </Col>
                          </Row>

                          {estimatedRunOutDate && (
                            <p className="small text-muted mb-3 text-center">
                              Based on this bag and schedule, we estimate it
                              will run out around{" "}
                              <strong>{estimatedRunOutDate}</strong>.
                            </p>
                          )}
                        </>
                      )}

                      {/* STEP 3: weight / life stage / goals / snapshot */}
                      {advancedStep === 3 && (
                        <>
                          <h5 className="mt-3 text-center">
                            Calories &amp; goals (advanced)
                          </h5>

                          <Row className="mb-3 justify-content-center">
                            <Col xs={12} md={5} className="mb-2">
                              <Form.Group>
                                <Form.Label>Current weight (lb)</Form.Label>
                                <Form.Control
                                  type="number"
                                  min="0"
                                  step="0.5"
                                  value={weightLb}
                                  onChange={(e) => setWeightLb(e.target.value)}
                                  isInvalid={!isSimple && advancedStep === 3 && !!stepError && !isValidWeight()}
                                />
                                <Form.Control.Feedback type="invalid">
                                  Weight is required.
                                </Form.Control.Feedback>

                              </Form.Group>
                            </Col>

                            <Col xs={12} md={5} className="mb-2">
                              <Form.Group>
                                <Form.Label>Ideal weight (lb)</Form.Label>
                                <Form.Control
                                  type="number"
                                  min="0"
                                  step="0.5"
                                  value={idealWeightLb}
                                  onChange={(e) =>
                                    setIdealWeightLb(e.target.value)
                                  }
                                />
                              </Form.Group>
                            </Col>
                          </Row>

                          <Row className="mb-3 justify-content-center">
                            <Col xs={12} md={5} className="mb-2">
                              <Form.Group>
                                <Form.Label>Life stage</Form.Label>
                                <Form.Select
                                  value={lifeStage}
                                  onChange={(e) =>
                                    setLifeStage(e.target.value)
                                  }
                                >
                                  <option value="puppy">
                                    Puppy / Kitten
                                  </option>
                                  <option value="adult">Adult</option>
                                  <option value="senior">Senior</option>
                                </Form.Select>
                              </Form.Group>
                            </Col>

                            <Col xs={12} md={5} className="mb-2">
                              <Form.Group>
                                <Form.Label>Activity level</Form.Label>
                                <Form.Select
                                  value={activityLevel}
                                  onChange={(e) =>
                                    setActivityLevel(e.target.value)
                                  }
                                >
                                  <option value="low">Low</option>
                                  <option value="normal">Normal</option>
                                  <option value="high">High</option>
                                </Form.Select>
                              </Form.Group>
                            </Col>
                          </Row>

                          <Row className="mb-3 justify-content-center">
                            <Col xs={12} md={5} className="mb-2">
                              <Form.Group>
                                <Form.Label>Goal</Form.Label>
                                <Form.Select
                                  value={goal}
                                  onChange={(e) =>
                                    setGoal(e.target.value)
                                  }
                                >
                                  <option value="maintain">
                                    Maintain weight
                                  </option>
                                  <option value="lose">
                                    Lose weight
                                  </option>
                                  <option value="gain">
                                    Gain weight
                                  </option>
                                </Form.Select>
                              </Form.Group>
                            </Col>

                            <Col
                              xs={12}
                              md={5}
                              className="mb-2 d-flex align-items-end"
                            >
                              <div className="small text-muted">
                                Species:{" "}
                                <strong>
                                  {species || "not set"}
                                </strong>{" "}
                                (set in the food section above)
                              </div>
                            </Col>
                          </Row>

                          <div className="mb-3">
                            <p className="small mb-1">
                              <strong>Calorie snapshot</strong>
                            </p>
                            <p className="small mb-1">
                              Maintenance:{" "}
                              {maintenanceCalories != null
                                ? `${maintenanceCalories} kcal/day`
                                : "Add weight, species, and life stage to see this."}
                            </p>
                            <p className="small mb-1">
                              Target for goal:{" "}
                              {targetCalories != null
                                ? `${targetCalories} kcal/day`
                                : "Set a goal to see a target."}
                            </p>
                            <p className="small mb-1">
                              From this food:{" "}
                              {currentCaloriesFromFood != null
                                ? `${currentCaloriesFromFood} kcal/day`
                                : "Add calories per cup and let Paw set cups per meal to see this."}
                            </p>
                            <p className="small text-muted mt-2 mb-0">
                              {riskMessage}
                            </p>
                          </div>
                        </>
                      )}
                    </>
                  )}

                  {/* AI Feeding Assistant – only on final step */}
                  {currentStep === maxStep && (
                    <Card className="feeding-ai-card mt-3">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <div className="small text-uppercase fw-semibold text-muted mb-1">
                              AI Feeding Assistant
                            </div>
                            <h6 className="mb-1">
                              Let Paw draft three starter plans
                            </h6>
                            {/* ✨ Generating overlay */}
{aiLoading && (
  <div className="feeding-ai-generating mt-2">
    <div className="feeding-ai-generating-row">
      <div className="feeding-ai-sparkle" aria-hidden="true">✦</div>
      <div className="small">
        <strong>Paw is generating…</strong>
        <div className="text-muted small">{aiStage || "Thinking…"}</div>
      </div>
    </div>

    <div className="feeding-ai-progress">
      <div
        className="feeding-ai-progress-bar"
        style={{ width: `${aiProgress}%` }}
      />
    </div>

    <div className="small text-muted mt-1 text-end">
      {aiProgress ? `${aiProgress}%` : ""}
    </div>
  </div>
)}
<AnimatePresence>
  {aiJustFinished && !aiLoading && (
    <motion.div
      className="feeding-ai-complete"
      variants={aiCompleteVariants}
      initial="hidden"
      animate="show"
      exit="exit"
    >
      <span className="feeding-ai-complete-badge">✨ Completed ✓</span>
    </motion.div>
  )}
</AnimatePresence>


                            <p className="mb-2 small text-muted">
                              Paw uses your pet’s weight, goal, meals per day,
                              and food details.{" "}
                              {isSimple
                                ? "(from Simple mode)"
                                : "(from Advanced mode)"}
                            </p>
                          </div>
                        </div>

                        {aiError && (
                          <Alert
                            variant="warning"
                            className="py-1 px-2 mb-2 feeding-ai-alert"
                            onClose={() => setAiError("")}
                            dismissible
                          >
                            <small>{aiError}</small>
                          </Alert>
                        )}

                        {aiPlanOptions.length > 0 && (
                          <div className="mt-3 feeding-ai-options-wrapper">
                            <div className="small mb-1">
                              <strong>Choose a plan</strong>. Selecting an
                              option will update the fields above.
                            </div>

                            {aiExplanation && (
                              <div className="small text-muted mb-2 feeding-ai-explanation">
                                {aiExplanation}
                              </div>
                            )}
<motion.div
  className="feeding-ai-options"
  variants={aiListVariants}
  initial="hidden"
  animate="show"
>
  <AnimatePresence>
    {aiPlanOptions.map((opt, index) => {
      const s = opt.plan.schedule || {};
      return (
        <motion.div
          key={opt.id || index}
          variants={aiItemVariants}
          className={`feeding-ai-option ${
            selectedPlanIndex === index ? "selected" : ""
          }`}
          role="button"
          tabIndex={0}
          onClick={() => handleSelectPlan(index)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleSelectPlan(index);
            }
          }}
        >
          <div className="d-flex">
            <Form.Check
              type="radio"
              id={`plan-option-${index}`}
              name="plan-options"
              className="me-2 mt-1"
              checked={selectedPlanIndex === index}
              onChange={() => handleSelectPlan(index)}
            />

            <div>
              <div className="small fw-semibold">{opt.label}</div>

              {opt.tagline && (
                <div className="small text-muted mb-1">{opt.tagline}</div>
              )}

              <div className="small">
                {(() => {
                  const meals = Number(s.mealsPerDay ?? 2) || 2;
                  const cupsArr = getMealCupsFromSchedule(s);
                  const labels = getMealLabels(meals);

                  // per-meal derived (fallback if cupsArr missing)
                  const total =
                    Number(s.totalDailyCups ?? 0) ||
                    (Number(s.morningCups ?? 0) + Number(s.eveningCups ?? 0));
                  const perMeal =
                    meals > 0 && total > 0
                      ? Number((total / meals).toFixed(2))
                      : 0;

                  return (
                    <div className="small">
                      Meals per day:{" "}
                      <strong className="feeding-ai-highlight">{meals}</strong>

                      {cupsArr.length > 0 ? (
                        <div className="mt-1">
                          {cupsArr.map((cups, i) => (
                            <div key={i}>
                              • {labels[i] || `Meal ${i + 1}`}:{" "}
                              <strong className="feeding-ai-highlight">
                                {Number(cups).toFixed(2)} cups
                              </strong>
                            </div>
                          ))}
                        </div>
                      ) : total > 0 ? (
                        <div className="mt-1">
                          <div>
                            • Per meal:{" "}
                            <strong className="feeding-ai-highlight">
                              {perMeal.toFixed(2)} cups
                            </strong>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </motion.div>
      );
    })}
  </AnimatePresence>
</motion.div>

                            <p className="small text-muted mt-2 mb-3">
                              These are general starting points. Always confirm
                              big feeding changes with your veterinarian.
                            </p>
                          </div>
                        )}

                        <div className="d-flex justify-content-end mt-2">
                          <div className="text-end">
                         <Button
  type="button"
  size="sm"
  className={`lpp-pill-btn-outline ${aiLoading ? "feeding-ai-btn-live" : ""}`}
  onClick={handleGenerateWithAI}
  disabled={!canGenerateAi || aiLoading}
>
  {aiLoading ? (
    <>
      <Spinner animation="border" size="sm" className="me-2" />
      Generating…
    </>
  ) : (
    "AI Generate"
  )}
</Button>


                            {!isSubActive && freeAiUsed && (
                                  <div className="small text-muted mt-2 text-end">
                                    You’ve used your free AI plan.{" "}
                                    <button
                                      type="button"
                                      className="btn btn-link p-0 align-baseline"
                                      onClick={goSubscribe}
                                    >
                                      Subscribe to unlock more
                                    </button>
                                    .
                                  </div>
                                )}



                            {!canUseAi && !aiLoading && (
                              <div className="small text-muted mt-1 text-end">
                                Add your pet’s weight and goal to unlock this.
                              </div>
                            )}
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  )}

                  {/* Footer: step navigation + save */}
              {/* Save row – below AI card */}
<div className="d-flex flex-wrap justify-content-between align-items-center mt-3 gap-2 feeding-footer">
  {/* Left side: clear + cancel */}
  <div className="d-flex gap-2 align-items-center">
  <Button
  type="button"
  className="lpp-icon-btn"
  onClick={handleResetForm}
  aria-label="Clear all fields"
  title="Clear all fields"
>
  🧹
</Button>


    <Button
      type="button"
      variant="link"
      size="sm"
      className="feeding-cancel-btn"
      onClick={() => setViewMode("summary")}
    >
      Cancel &amp; view summary
    </Button>
  </div>

  {/* Right side: step nav + save */}
  <div className="d-flex align-items-center gap-2 feeding-footer-right">
    {currentStep > 1 && (
   <Button
        type="button"
        variant="outline-secondary"
        size="sm"
        className="feeding-secondary-btn"
        onClick={goToPrevStep}
      >
        Back
      </Button>

    )}

    {currentStep < maxStep ? (
      <Button
        type="button"
        size="sm"
        className="lpp-pill-btn feeding-primary-btn"
        onClick={handleNextStep}

      >
        Next
      </Button>
    ) : (
      <>
        {success && (
          <div className="feeding-save-status">
            {success}
          </div>
        )}
        <Button
          type="submit"
          disabled={saving}
          className="lpp-pill-btn feeding-primary-btn"
        >
          <FontAwesomeIcon icon={faSave} />
          {saving ? "  Saving…" : "  Save feeding plan"}
        </Button>
      </>
    )}
  </div>
</div>

                </Form>
              </Card.Body>

            </Card>
          )}

        </main>
<div >
          <FloatingPawButton isAuthenticated={true} hasActiveSub={true} />
        </div>
      </div>

    </div>
  );
}
